import React, { useState, useEffect } from 'react';
import { Modal } from '@mui/material';
import { X, Server, FileCode, Layers, Milestone, Loader2, Save, Activity } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';

interface AgentConfigModalProps {
    open: boolean;
    onClose: () => void;
    agent: {
        id: string;
        hostname?: string;
    } | null;
}

interface ConfigData {
    cluster_name: string;
    k8s_source_type: string;
    yaml_file_path: string;
    kube_context: string;
    kubernetes_version: string;
}

const AgentConfigModal: React.FC<AgentConfigModalProps> = ({ open, onClose, agent }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<ConfigData>({
        cluster_name: '',
        k8s_source_type: '',
        yaml_file_path: '',
        kube_context: '',
        kubernetes_version: ''
    });

    useEffect(() => {
        if (open && agent?.id) {
            fetchConfig();
        }
    }, [open, agent]);

    const fetchConfig = async () => {
        if (!agent?.id) return;
        setLoading(true);
        try {
            const response = await baseapi.get(`k8s_clusters/${agent.id}/config`);
            const data = response.data?.data || response.data;
            if (data) {
                setFormData({
                    cluster_name: data.cluster_name || '',
                    k8s_source_type: data.k8s_source_type || '',
                    yaml_file_path: data.yaml_file_path || '',
                    kube_context: data.kube_context || '',
                    kubernetes_version: data.kubernetes_version || ''
                });
            } else {
                resetForm();
            }
        } catch (error: any) {
            console.error('Error fetching agent k8s config:', error);
            resetForm();
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            cluster_name: '',
            k8s_source_type: '',
            yaml_file_path: '',
            kube_context: '',
            kubernetes_version: ''
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!agent?.id) return;

        setSaving(true);
        try {
            const response = await baseapi.post(`k8s_clusters/${agent.id}/config`, formData);
            if (response.data?.status || response.status === 200) {
                showSuccess(response.data?.message || 'Configuration saved successfully!');
                onClose();
            } else {
                showError(response.data?.message || 'Failed to save configuration.');
            }
        } catch (error: any) {
            console.error('Error saving agent k8s config:', error);
            showError(error.response?.data?.message || error.message || 'Failed to save configuration.');
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field: keyof ConfigData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Modal 
            open={open} 
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return;
                onClose();
            }}
        >
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[90%] sm:w-[540px] max-w-xl bg-gray-900/95 backdrop-blur-xl 
                border border-orange-500/20 shadow-2xl rounded-2xl text-white
                relative overflow-hidden max-h-[90vh] flex flex-col z-[10000]">
                
                {/* Subtle orange accent top line */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/40 to-transparent"></div>

                {/* Modal Header */}
                <div className="p-5 border-b border-gray-800 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="bg-orange-500/20 p-1.5 rounded-lg border border-orange-500/30">
                            <Server className="w-5 h-5 text-orange-400" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-white leading-none">
                                Kubernetes Cluster Config
                            </h2>
                            <p className="text-[10px] text-gray-400 mt-1">
                                Agent: {agent?.hostname || 'Unknown'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors bg-gray-800/50 hover:bg-gray-800 p-1.5 rounded-lg border border-gray-700/50"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Modal Content */}
                {loading ? (
                    <div className="py-20 flex flex-col items-center justify-center gap-3">
                        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                        <p className="text-xs text-gray-400 font-medium">Loading config details...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto custom-scroll p-5 space-y-4">
                        {/* Cluster Name */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wider text-orange-400 flex items-center gap-1.5">
                                <Server className="w-3.5 h-3.5" />
                                Cluster Name
                            </label>
                            <input
                                type="text"
                                value={formData.cluster_name}
                                onChange={(e) => handleInputChange('cluster_name', e.target.value)}
                                placeholder="e.g. production-cluster"
                                className="w-full bg-gray-800/40 border border-gray-700/80 rounded-lg px-3 py-2 text-xs text-white focus:border-orange-500/50  /30 outline-none transition-all"
                            />
                        </div>

                        {/* K8s Source Type */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wider text-orange-400 flex items-center gap-1.5">
                                <Activity className="w-3.5 h-3.5" />
                                K8s Source Type
                            </label>
                            <input
                                type="text"
                                value={formData.k8s_source_type}
                                onChange={(e) => handleInputChange('k8s_source_type', e.target.value)}
                                placeholder="e.g. lwt_fetch"
                                className="w-full bg-gray-800/40 border border-gray-700/80 rounded-lg px-3 py-2 text-xs text-white focus:border-orange-500/50  /30 outline-none transition-all"
                            />
                        </div>

                        {/* YAML File Path */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wider text-orange-400 flex items-center gap-1.5">
                                <FileCode className="w-3.5 h-3.5" />
                                YAML File Path
                            </label>
                            <input
                                type="text"
                                value={formData.yaml_file_path}
                                onChange={(e) => handleInputChange('yaml_file_path', e.target.value)}
                                placeholder="e.g. /home/runner/.kube/config"
                                className="w-full bg-gray-800/40 border border-gray-700/80 rounded-lg px-3 py-2 text-xs text-white focus:border-orange-500/50  /30 outline-none transition-all"
                            />
                        </div>

                        {/* Kube Context */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wider text-orange-400 flex items-center gap-1.5">
                                <Layers className="w-3.5 h-3.5" />
                                Kube Context
                            </label>
                            <input
                                type="text"
                                value={formData.kube_context}
                                onChange={(e) => handleInputChange('kube_context', e.target.value)}
                                placeholder="e.g. my-cluster-context"
                                className="w-full bg-gray-800/40 border border-gray-700/80 rounded-lg px-3 py-2 text-xs text-white focus:border-orange-500/50  /30 outline-none transition-all"
                            />
                        </div>

                        {/* Kubernetes Version */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wider text-orange-400 flex items-center gap-1.5">
                                <Milestone className="w-3.5 h-3.5" />
                                Kubernetes Version
                            </label>
                            <input
                                type="text"
                                value={formData.kubernetes_version}
                                onChange={(e) => handleInputChange('kubernetes_version', e.target.value)}
                                placeholder="e.g. 1.29"
                                className="w-full bg-gray-800/40 border border-gray-700/80 rounded-lg px-3 py-2 text-xs text-white focus:border-orange-500/50  /30 outline-none transition-all"
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-700 text-gray-300 rounded-lg text-xs font-bold hover:bg-gray-800 hover:text-white transition-all"
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="flex items-center gap-1.5 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all shadow-lg shadow-orange-950/20 disabled:opacity-55"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-3.5 h-3.5" />
                                        Save Config
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Modal>
    );
};

export default AgentConfigModal;
